import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ReplaySubject } from 'rxjs';

import { RingColorsService } from 'app/modules/social/services/ring-colors/ring-colors.service';
import { Loggable } from 'app/utils/logging/loggable';
import BadgeColor from 'entity/BadgeColor';
import BadgeColorConfiguration from 'entity/BadgeColorConfiguration';
import BadgeColorPalette from 'entity/BadgeColorPalette';
import BadgeQuestion from 'entity/BadgeQuestion';
import RingColor from 'entity/RingColor';
import SimpleColorConfiguration from 'entity/SimpleColorConfiguration';
import QuestionInputType from 'enums/QuestionInputType';

interface CustomOption extends RingColor {
  title: string;
}

@Component({
  selector: 'app-visual-indicator',
  templateUrl: './visual-indicator.component.html',
  styleUrl: './visual-indicator.component.scss',
  standalone: false,
})
export class VisualIndicatorComponent extends Loggable implements OnDestroy, OnInit, OnChanges {
  @Input() public customQuestion: BadgeQuestion[] = [];
  @Input() public roomIndicator?: SimpleColorConfiguration;
  @Output() public visualIndicator = new EventEmitter<SimpleColorConfiguration>();
  @Output() public navigate = new EventEmitter<string>();
  private destroyed$ = new ReplaySubject<boolean>(1);
  protected colorPaletteId = '';
  protected selectedQuestion?: BadgeQuestion;
  protected customOptionColor: CustomOption[] = [];
  protected singleSelectQuestions: BadgeQuestion[] = [];

  protected visualIndicatorColors: BadgeColor[] = [];

  protected selectingColors = false;

  public constructor(
    private ringColorsService: RingColorsService,
    private route: ActivatedRoute,
  ) {
    super();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.roomIndicator) {
      this.initialize();
    }
    if (changes.customQuestion) {
      this.initialize();
    }
  }

  public ngOnInit(): void {
    const roomSetId = this.route.snapshot.paramMap.get('id');
    if (!roomSetId) {
      return;
    }
    this.getColorPalette(roomSetId).catch((err) => this.error(err));
    this.initialize();
  }

  private initialize() {
    this.customOptionColor = [];
    this.selectedQuestion = undefined;
    this.selectingColors = false;

    const singleSelectQuestions = this.customQuestion.filter(
      (question) => question.inputType === QuestionInputType.SINGLE_SELECT,
    );
    this.singleSelectQuestions = singleSelectQuestions;

    if (!this.roomIndicator || !this.roomIndicator?.colors) {
      return;
    }

    const singleQuestion = singleSelectQuestions.find((question) => question.title === this.roomIndicator?.property);

    if (!singleQuestion) {
      return;
    }

    this.selectedQuestion = singleQuestion;

    if (!singleQuestion.options) {
      return;
    }

    this.customOptionColor = singleQuestion.options?.map((option) => {
      const indicatorItem = this.roomIndicator?.colors?.find((color) => color.description === option);
      return {
        title: indicatorItem ? indicatorItem.description || '' : option,
        colorNumber: indicatorItem && indicatorItem.colorNumber,
        color: indicatorItem && indicatorItem.color,
        description: indicatorItem ? indicatorItem.description : option,
      };
    });

    this.selectingColors = true;

    this.emitVisualIndicator();
  }

  protected goToSelectColors() {
    if (!this.selectedQuestion?.options) {
      this.customOptionColor = [];
      return;
    }
    this.customOptionColor = this.selectedQuestion?.options?.map((option, index) => {
      const color = this.visualIndicatorColors[index] || {
        colorNumber: 0,
        color: 'undefined',
      };
      return {
        title: option,
        colorNumber: color.colorNumber,
        color: color.color,
        description: option,
      };
    });
    this.emitVisualIndicator();
    this.selectingColors = true;
  }

  protected changeColor(index: number, color: RingColor): void {
    this.customOptionColor[index] = {
      ...this.customOptionColor[index],
      ...color,
    };
    this.emitVisualIndicator();
  }

  private emitVisualIndicator(): void {
    const visualIndicator: BadgeColorConfiguration = this.mapVisualIndicatorConfig();
    this.visualIndicator.emit(visualIndicator);
  }

  private mapVisualIndicatorConfig(): SimpleColorConfiguration {
    const value: SimpleColorConfiguration = {
      colorPaletteId: this.colorPaletteId,
      colors: [],
      colorMap: {},
      property: this.selectedQuestion?.title,
      fromComplex: false,
    };

    this.customOptionColor.forEach(({ color, colorNumber, description }) => {
      value.colors?.push({
        colorNumber,
        color,
        description,
      });

      if (colorNumber !== undefined && description && value.colorMap) {
        value.colorMap[description] = colorNumber;
      }
    });

    return value;
  }

  private async getColorPalette(roomSetId: string) {
    const colorPaletteCofig: BadgeColorConfiguration = await this.ringColorsService.getColorPaletteConfig(roomSetId);
    // TODO: remove hardcoded colorPaletteId
    const colorPaletteId = colorPaletteCofig.colorPaletteId || '4c467814-3d3f-4804-92c7-7cd7c1ab68f8';
    const colorPalette: BadgeColorPalette = await this.ringColorsService.getColorPalette(colorPaletteId);

    if (!colorPalette || !colorPalette.badgeColorPaletteId || !colorPalette.colors) {
      return;
    }

    this.visualIndicatorColors = colorPalette.colors;
    this.visualIndicatorColors.push({
      color: undefined,
      colorNumber: undefined,
    });
    this.colorPaletteId = colorPalette.badgeColorPaletteId;
  }

  protected changeQuestion() {
    if (this.selectingColors) {
      this.goToSelectColors();
    }
    if (!this.selectedQuestion) {
      this.selectingColors = false;
      this.visualIndicator.emit();
    }
  }

  protected goToCreateQuestion() {
    this.navigate.emit('question');
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
